import React, { useState, useEffect } from 'react';
import { X, Send, Save, Loader2, AlertTriangle, Youtube, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PlatformSelector from './components/PlatformSelector';
import CharacterCounter from './components/CharacterCounter';
import MediaUploader from './components/MediaUploader';
import SchedulePicker from './components/SchedulePicker';
import PostPreview from './components/PostPreview';
import {
    getCharacterLimit,
    validatePostContent
} from '../../utils/socialMediaHelpers';
import {
    createPost,
    createCrossPost,
    updatePost,
    getIntegrations
} from '../../api/socialMedia';

const PostComposer = ({
    isOpen,
    onClose,
    editingPost = null,
    initialDate = null,
    onSave,
    currentBrandId = null,
    draftSeed = null
}) => {
    // Form state
    const [content, setContent] = useState('');
    const [selectedIntegrationIds, setSelectedIntegrationIds] = useState([]);
    const [mediaUrls, setMediaUrls] = useState([]);
    const [scheduledAt, setScheduledAt] = useState(null);
    const [platformSettings, setPlatformSettings] = useState({});

    // UI state
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showPreview, setShowPreview] = useState(true);

    // Load integrations
    useEffect(() => {
        const loadIntegrations = async () => {
            try {
                const filters = currentBrandId ? { brand_id: currentBrandId } : {};
                const response = await getIntegrations(filters);
                // API returns { integrations: [...], total: N }
                const integrationsList = response.integrations || response.data || [];
                setIntegrations(integrationsList);
            } catch (err) {
                console.error('Failed to load integrations:', err);
                setError('Failed to load connected accounts');
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            loadIntegrations();
        }
    }, [isOpen, currentBrandId]);

    // Initialize form when editing or setting initial date
    useEffect(() => {
        if (editingPost) {
            setContent(editingPost.content || '');
            setSelectedIntegrationIds(editingPost.integration_id ? [editingPost.integration_id] : []);
            setMediaUrls(editingPost.media_urls || []);
            setScheduledAt(editingPost.scheduled_at || null);
            setPlatformSettings(editingPost.platform_settings || {});
        } else if (draftSeed && isOpen) {
            setContent(draftSeed.content || '');
            setSelectedIntegrationIds([]);
            setMediaUrls(draftSeed.mediaUrls || []);
            setScheduledAt(initialDate || null);
            setPlatformSettings({});
        } else {
            // Reset form
            setContent('');
            setSelectedIntegrationIds([]);
            setMediaUrls([]);
            setScheduledAt(initialDate || null);
            setPlatformSettings({});
        }
    }, [editingPost, draftSeed, initialDate, isOpen]);

    // Calculate character limit based on selected platforms
    const selectedPlatforms = selectedIntegrationIds
        .map(id => integrations.find(i => i.id === id)?.platform)
        .filter(Boolean);
    const characterLimit = getCharacterLimit(selectedPlatforms);

    // Check if YouTube is selected (needs special settings)
    const hasYouTube = selectedPlatforms.includes('youtube');

    // Get account name for preview
    const selectedIntegration = integrations.find(i => selectedIntegrationIds.includes(i.id));
    const accountName = selectedIntegration?.display_name || selectedIntegration?.account_name || 'CloudMortgage.io';

    // Handle save
    const handleSave = async (asDraft = false) => {
        try {
            setError(null);

            // Validate
            const validation = validatePostContent(content, selectedPlatforms);
            if (!validation.valid) {
                setError(validation.errors.join('. '));
                return;
            }

            setSaving(true);

            const postData = {
                content,
                media_urls: mediaUrls.map(m => m.url),
                scheduled_at: asDraft ? null : scheduledAt,
                platform_settings: platformSettings,
                status: asDraft ? 'draft' : (scheduledAt ? 'scheduled' : 'publishing')
            };

            if (editingPost) {
                // Update existing post
                await updatePost(editingPost.id, postData);
            } else if (selectedIntegrationIds.length === 1) {
                // Single platform post
                await createPost({
                    ...postData,
                    integration_id: selectedIntegrationIds[0]
                });
            } else {
                // Cross-post to multiple platforms
                await createCrossPost({
                    ...postData,
                    integration_ids: selectedIntegrationIds
                });
            }

            onSave?.();
            onClose();
        } catch (err) {
            console.error('Failed to save post:', err);
            setError(err.message || 'Failed to save post');
        } finally {
            setSaving(false);
        }
    };

    // Handle YouTube settings
    const updateYouTubeSettings = (key, value) => {
        setPlatformSettings(prev => ({
            ...prev,
            youtube: {
                ...prev.youtube,
                [key]: value
            }
        }));
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    className={`w-full max-h-[90vh] bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden flex flex-col ${
                        showPreview ? 'max-w-6xl' : 'max-w-2xl'
                    }`}
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                            {editingPost ? 'Edit Post' : 'Create Post'}
                        </h2>
                        <div className="flex items-center gap-2">
                            {/* Preview Toggle */}
                            <button
                                onClick={() => setShowPreview(!showPreview)}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                    showPreview
                                        ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400'
                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                                }`}
                            >
                                {showPreview ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                Preview
                            </button>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 flex overflow-hidden">
                        {/* Editor Panel */}
                        <div className={`flex-1 overflow-y-auto p-6 space-y-6 ${showPreview ? 'border-r border-slate-200 dark:border-slate-700' : ''}`}>
                            {/* Error display */}
                            <AnimatePresence>
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3"
                                    >
                                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-red-700 dark:text-red-400 flex-1">{error}</p>
                                        <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                                            ×
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Platform Selector */}
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
                                </div>
                            ) : (
                                <PlatformSelector
                                    integrations={integrations}
                                    selectedIntegrationIds={selectedIntegrationIds}
                                    onSelectionChange={setSelectedIntegrationIds}
                                    disabled={saving}
                                />
                            )}

                            {/* Content textarea */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Content
                                </label>
                                <textarea
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    placeholder="What would you like to share?"
                                    disabled={saving}
                                    className="w-full h-40 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                />
                                <CharacterCounter
                                    current={content.length}
                                    limit={characterLimit}
                                />
                            </div>

                            {/* Media Uploader */}
                            <MediaUploader
                                mediaUrls={mediaUrls}
                                onMediaChange={setMediaUrls}
                                disabled={saving}
                            />

                            {/* Schedule Picker */}
                            <SchedulePicker
                                scheduledAt={scheduledAt}
                                onScheduleChange={setScheduledAt}
                                disabled={saving}
                            />

                            {/* Advanced Settings (YouTube-specific) */}
                            {hasYouTube && (
                                <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        className="w-full px-4 py-3 flex items-center justify-between text-left bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                    >
                                        <div className="flex items-center gap-2">
                                            <Youtube className="w-4 h-4 text-red-500" />
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                YouTube Settings
                                            </span>
                                        </div>
                                        {showAdvanced ? (
                                            <ChevronUp className="w-4 h-4 text-slate-400" />
                                        ) : (
                                            <ChevronDown className="w-4 h-4 text-slate-400" />
                                        )}
                                    </button>

                                    <AnimatePresence>
                                        {showAdvanced && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className="overflow-hidden"
                                            >
                                                <div className="p-4 space-y-4">
                                                    {/* Video Title */}
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                                            Video Title
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={platformSettings.youtube?.title || ''}
                                                            onChange={(e) => updateYouTubeSettings('title', e.target.value)}
                                                            placeholder="Enter video title"
                                                            maxLength={100}
                                                            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                                                        />
                                                    </div>

                                                    {/* Privacy Setting */}
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                                            Privacy
                                                        </label>
                                                        <select
                                                            value={platformSettings.youtube?.privacy || 'public'}
                                                            onChange={(e) => updateYouTubeSettings('privacy', e.target.value)}
                                                            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                                                        >
                                                            <option value="public">Public</option>
                                                            <option value="unlisted">Unlisted</option>
                                                            <option value="private">Private</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}
                        </div>

                        {/* Preview Panel */}
                        {showPreview && (
                            <div className="w-96 flex-shrink-0 overflow-hidden">
                                <PostPreview
                                    content={content}
                                    mediaUrls={mediaUrls}
                                    selectedPlatforms={selectedPlatforms}
                                    accountName={accountName}
                                    platformSettings={platformSettings}
                                />
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
                        <button
                            onClick={() => handleSave(true)}
                            disabled={saving || selectedIntegrationIds.length === 0}
                            className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save className="w-4 h-4" />
                            Save as Draft
                        </button>

                        <div className="flex items-center gap-3">
                            <button
                                onClick={onClose}
                                disabled={saving}
                                className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleSave(false)}
                                disabled={saving || selectedIntegrationIds.length === 0 || content.trim().length === 0}
                                className="px-5 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : scheduledAt ? (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Schedule
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Publish Now
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default PostComposer;
